import { useCallback, useEffect, useState } from 'react'

import { useCloseModal, useConfigureModalController, useModalController } from '@/atoms'
import { getOrder, selectOrders } from '@/features/domain/order'
import { selectUserConfiguration } from '@/features/domain/user'
import { useNotification, useReadOnly } from '@/hooks'
import {
  extractExtendedDropoffFromOrder,
  extractExtendedPickupFromOrder,
} from '@/local/server-data/domain/server/factories/orders'
import { store, useAppDispatch } from '@/store'

import type { TabValues } from '../../../typings'
import { useTexts } from '../useTexts'

type ModalStatus = 'pending' | 'ready'
type ModalData = {
  tab: TabValues
  getRecurrentOrder: (id: string, date: string) => Promise<void>
  recurrentOrderId: string
  date: string
  type: uui.domain.client.rm.OrderStepIdentifier
  loadingData: boolean
  extOrderStep?: uui.domain.client.rm.ExtendedOrderStep
  orderInArchivedDay: boolean
}

export const modalId = 'orderPreviewModal'
export const useController = () => useModalController<ModalData, ModalStatus>(modalId)

export const useConfigureController = (
  recurrentOrderId: string,
  date: string,
  type: uui.domain.client.rm.OrderStepIdentifier,
) => {
  const { readonly } = useReadOnly()
  const dispatch = useAppDispatch()
  const toast = useNotification()
  const close = useCloseModal()
  const texts = useTexts()

  const getRecurrentOrder = useCallback(async (orderId: string, date: string) => {
    updateData({ loadingData: true })

    const state = store.getState()
    const { startFromDate, planType } = selectUserConfiguration(state)

    let extOrder: uui.domain.client.rm.ExtendedOrder | null = null
    const orderInArchivedDay = date < startFromDate
    const inArchivedRoutePlan = planType === 'archived'

    // Determine if the order must be loaded remotely
    // If the order is in an archived route plan or the calendar date is in an archived day, it must be loaded remotely
    const mustLoadOrderRemotely = inArchivedRoutePlan || orderInArchivedDay

    if (mustLoadOrderRemotely) {
      const result = await dispatch(getOrder({ orderId, date }))

      if (getOrder.rejected.match(result)) {
        toast.error(texts.orderNotFoundError)

        updateData({ loadingData: false })
        return
      }
      extOrder = result.payload
    } else {
      const extendedOrders = selectOrders(state)
      extOrder = extendedOrders[orderId]
    }

    if (!extOrder) {
      toast.error(texts.orderNotFoundError)
      updateData({ loadingData: false })
      return
    }

    switch (type) {
      case 'd':
      case 's':
        if (extOrder.type === 'p') {
          throw new Error('Cannot get recurrent order of type pickup')
        }

        updateData({
          extOrderStep: extractExtendedDropoffFromOrder(extOrder),
          orderInArchivedDay,
          date,
        })
        break

      case 'p':
        if (extOrder.type !== 'p' && extOrder.type !== 'pd') {
          throw new Error('Cannot get recurrent order of type pickup')
        }
        updateData({
          extOrderStep: extractExtendedPickupFromOrder(extOrder),
          orderInArchivedDay,
          date,
        })
        break
    }

    updateData({ loadingData: false })
  }, [])

  // INITIALIZATION

  const [options] = useState(() => ({
    createInitialState: () => ({
      status: 'ready' as ModalStatus,
      disabled: false,
      invalid: false,
      data: {
        tab: 0 as TabValues,
        recurrentOrderId,
        type,
        date,
        getRecurrentOrder,
        loadingData: false,
        orderInArchivedDay: false,
      },
      close,
    }),
  }))

  const ctrl = useConfigureModalController<ModalData, ModalStatus>(modalId, options)
  const { update, updateData, status } = ctrl

  // EFFECTS

  useEffect(() => {
    switch (readonly) {
      case true:
        update({ status: 'pending' })
        break

      case false:
        if (status !== 'pending') return

        update({ status: 'ready' })
        break
    }
  }, [readonly, status, update])

  useEffect(() => {
    if (recurrentOrderId && type && getRecurrentOrder) {
      getRecurrentOrder(recurrentOrderId, date)
    }
  }, [recurrentOrderId, date, type, getRecurrentOrder])
  return ctrl
}
